import { useCallback, useEffect, useState } from 'react';
import { useWSEvent } from './hooks';

// "A new version is available" detection. This module is bundled into the main
// entry chunk, so its own URL IS the build's version (Vite content-hashes the
// entry: /assets/index-<hash>.js). /api/version reports what the server is
// serving now; a mismatch means a deploy happened since this tab loaded.
//
// Checked on each WS `hello` (a deploy restarts the backend, so every socket
// reconnects right after one) and when the tab regains focus. In dev the entry
// is /src/... and the server reports null — never a mismatch.
const OWN_ENTRY = new URL(import.meta.url).pathname;
const MIN_INTERVAL_MS = 30_000;

export function useNewVersionAvailable(): boolean {
  const [stale, setStale] = useState(false);
  const [lastCheck, setLastCheck] = useState(0);

  const check = useCallback(() => {
    const now = Date.now();
    if (stale || now - lastCheck < MIN_INTERVAL_MS) return;
    setLastCheck(now);
    fetch('/api/version', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((v: { entry?: string | null } | null) => {
        if (v?.entry && v.entry !== OWN_ENTRY) setStale(true);
      })
      .catch(() => {});
  }, [stale, lastCheck]);

  useWSEvent('hello', () => check());
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [check]);

  return stale;
}
